import React, { useState, useEffect, useContext } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Paper,
  Radio,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  InputAdornment,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { AuthContext } from '../../context/auth-context';
import ProjectsAPI from '../../../services/api/admin/projects-api';
import Logger from '../../../utils/logger';

/**
 * ProjectWorkflowEditorModal - Dialog to edit a project's native local workflow.
 */
const ProjectWorkflowEditorModal = ({
  open,
  handleClose,
  projectId,
  projectName,
  projectNumber,
}) => {
  const auth = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [nativeWorkflow, setNativeWorkflow] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [validationError, setValidationError] = useState('');

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch workflows for the project
  useEffect(() => {
    const fetchWorkflows = async () => {
      if (!open || !projectId) return;

      setLoading(true);
      setValidationError('');
      setStatuses([]);
      setNativeWorkflow(null);

      try {
        Logger.debug('Fetching workflows for project', { projectId });
        const workflowsData = await ProjectsAPI.getWorkflow(
          projectId,
          auth.token
        );
        setWorkflows(workflowsData);

        // Find the native local workflow
        const native = workflowsData.find((w) => w.is_native === true);
        if (native) {
          setNativeWorkflow(native);
          // Sort statuses by sequencenumber
          const sortedStatuses = [...(native.statuses || [])].sort(
            (a, b) => (a.sequencenumber || 0) - (b.sequencenumber || 0)
          );
          setStatuses(sortedStatuses);
        } else {
          Logger.warn('No native local workflow found for project', {
            projectId,
          });
        }
      } catch (err) {
        Logger.error('Error fetching project workflows', err);
        setSnackbar({
          open: true,
          message:
            err.response?.data?.message || 'Failed to load project workflows.',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflows();
  }, [open, projectId, auth.token]);

  // Handle status name/label change
  const handleNameChange = (index, value) => {
    setStatuses((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], label: value, name: value };
      return updated;
    });
  };

  // Handle status swim lane type change
  const handleTypeChange = (index, value) => {
    setStatuses((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], swim_lane_type: value };
      return updated;
    });
  };

  // Handle status anchor change (exactly one allowed)
  const handleAnchorChange = (index) => {
    setStatuses((prev) =>
      prev.map((status, i) => ({
        ...status,
        is_field_installation_anchor: i === index,
      }))
    );
  };

  // Handle status default duration change
  const handleDurationChange = (index, value) => {
    setStatuses((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        default_duration_days: value === '' ? null : parseInt(value, 10),
      };
      return updated;
    });
  };

  // Validate changes
  const validateChanges = () => {
    const hasEmptyName = statuses.some(
      (s) => !s.label || s.label.trim() === ''
    );
    if (hasEmptyName) {
      return 'All swim lane names are required.';
    }

    const anchorCount = statuses.filter(
      (s) => !!s.is_field_installation_anchor
    ).length;
    if (anchorCount !== 1) {
      return 'Exactly one swim lane must be selected as the Field Installation Anchor.';
    }

    return '';
  };

  // Save changes
  const handleSave = async () => {
    const errorMsg = validateChanges();
    if (errorMsg) {
      setValidationError(errorMsg);
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error',
      });
      return;
    }

    setValidationError('');
    setSaving(true);
    Logger.userAction('Updating project workflow', {
      projectId,
      stepSetId: nativeWorkflow?.id,
    });

    try {
      await ProjectsAPI.updateWorkflow(
        projectId,
        nativeWorkflow.id,
        statuses,
        auth.token
      );

      setSnackbar({
        open: true,
        message: 'Project workflow updated successfully!',
        severity: 'success',
      });

      // Brief timeout before closing to let user see success snackbar
      setTimeout(() => {
        handleClose();
      }, 800);
    } catch (err) {
      Logger.error('Error saving project workflow changes', err);
      setValidationError(
        err.response?.data?.message || 'Failed to update workflow.'
      );
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to update workflow.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Find all non-native (ACC) workflows
  const accWorkflows = workflows.filter((w) => !w.is_native);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="span" fontWeight="bold">
          Edit Workflow — {projectName || 'Project'} ({projectNumber || 'N/A'})
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 6,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Read-Only ACC Workflows alert */}
            {accWorkflows.length > 0 && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                This project also has ACC-managed workflow(s):{' '}
                <strong>{accWorkflows.map((w) => w.name).join(', ')}</strong>{' '}
                (Read-only)
              </Alert>
            )}

            {validationError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {validationError}
              </Alert>
            )}

            {!nativeWorkflow && !loading ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No native local workflow found for this project.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell
                        sx={{ fontWeight: 'bold', width: '80px', py: 1.5 }}
                      >
                        #
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>
                        Swim Lane Name
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 'bold', width: '180px', py: 1.5 }}
                      >
                        Type
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 'bold', width: '100px', py: 1.5 }}
                        align="center"
                      >
                        Anchor
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 'bold', width: '180px', py: 1.5 }}
                      >
                        Default Duration
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statuses.map((status, index) => (
                      <TableRow key={status.id || index} hover>
                        {/* Sequence */}
                        <TableCell>
                          <Chip
                            label={status.sequencenumber || index + 1}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>

                        {/* Status Label (Name) */}
                        <TableCell>
                          <TextField
                            value={status.label || status.name || ''}
                            onChange={(e) =>
                              handleNameChange(index, e.target.value)
                            }
                            fullWidth
                            size="small"
                            required
                            error={!status.label || status.label.trim() === ''}
                          />
                        </TableCell>

                        {/* Swim Lane Type */}
                        <TableCell>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={status.swim_lane_type || 'standard'}
                              onChange={(e) =>
                                handleTypeChange(index, e.target.value)
                              }
                            >
                              <MenuItem value="standard">Standard</MenuItem>
                              <MenuItem value="vdc">VDC</MenuItem>
                              <MenuItem value="prefab">Prefabrication</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>

                        {/* Anchor Radio */}
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Radio
                              checked={!!status.is_field_installation_anchor}
                              onChange={() => handleAnchorChange(index)}
                              size="small"
                            />
                            {status.is_field_installation_anchor && (
                              <Tooltip title="Field Installation Anchor — Reference point for all due date calculations">
                                <span
                                  style={{ cursor: 'help', fontSize: '1.1rem' }}
                                >
                                  ⚓
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>

                        {/* Duration */}
                        <TableCell>
                          <TextField
                            type="number"
                            value={
                              status.default_duration_days != null
                                ? status.default_duration_days
                                : ''
                            }
                            onChange={(e) =>
                              handleDurationChange(index, e.target.value)
                            }
                            placeholder={
                              status.is_field_installation_anchor
                                ? 'Variable'
                                : '0'
                            }
                            disabled={!!status.is_field_installation_anchor}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  days
                                </InputAdornment>
                              ),
                              inputProps: { min: 0 },
                            }}
                            size="small"
                            fullWidth
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="outlined"
          startIcon={<CloseIcon />}
          onClick={handleClose}
          disabled={saving}
          sx={{ width: 120 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || !nativeWorkflow || loading}
          sx={{ width: 150 }}
        >
          {saving ? 'Saving...' : 'SAVE'}
        </Button>
      </DialogActions>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ProjectWorkflowEditorModal;
